import React, { useState, useEffect } from 'react';
import { db, auth } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { BigQueryService } from '../../services/bigQueryService';
import { useCatalogos } from '../../context/CatalogosContext';
import { CubeIcon, ClockIcon, ArrowPathIcon, StarIcon, ArrowTrendingUpIcon, ChartBarIcon, BellIcon } from '../ui/Icons';
import { estadoConfig } from '../ui/estadoPedido';
import useEsMovil from '../../hooks/useEsMovil';
import useConteoAnimado from '../../hooks/useConteoAnimado';
import { ESTILO_TILE, DonaEstados } from './widgetsUI';

// Estilos responsivos para móvil
const estilosResponsivos = `
  @media (max-width: 768px) {
    .estadisticas-pedidos-container {
      margin: 15px 0 !important;
    }

    .estadisticas-pedidos-header {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
      margin-bottom: 12px !important;
    }

    .estadisticas-pedidos-header h3 {
      font-size: 18px !important;
      margin: 0 !important;
    }

    .estadisticas-pedidos-header small {
      font-size: 11px !important;
    }

    .estadisticas-pedidos-header-der {
      width: 100%;
      flex-direction: column !important;
      gap: 8px !important;
    }

    .btn-actualizar-pedidos {
      width: 100% !important;
      padding: 10px 16px !important;
      font-size: 13px !important;
    }

    .pedidos-tiles {
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
    }

    .pedidos-tile {
      padding: 10px !important;
    }

    .pedidos-viz-grid {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }

    .viz-card-pedidos {
      padding: 12px !important;
    }

    .mensaje-motivacional {
      font-size: 11px !important;
    }
  }
`;

function EstadisticasPedidosWidget() {
  // Usuarios desde Context (sin queries en mount)
  const { usuarios: usuariosCtx } = useCatalogos();

  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usuarioData, setUsuarioData] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Esperar a que el Context tenga los usuarios cargados antes de buscar
    if (usuariosCtx.length > 0) {
      cargarEstadisticas(false);
    }
  }, [usuariosCtx]);

  const cargarEstadisticas = async (forzar = false) => {
    try {
      setLoading(true);
      const email = auth.currentUser?.email;
      if (!email) {
        setLoading(false);
        return;
      }

      // Buscar el usuario actual en el Context (sin query a Firestore)
      const userData = usuariosCtx.find(u => u.email === email);

      if (!userData) {
        setLoading(false);
        return;
      }

      if (userData.rol !== 'servicio_cliente') {
        setLoading(false);
        return;
      }

      setUsuarioData({
        nombre: userData.nombre,
        email: userData.email
      });

      const anio = new Date().getFullYear().toString();
      const mes = (new Date().getMonth() + 1).toString().padStart(2, '0');

      // Lectura DIRECTA del doc precalculado en Firestore (sin Cloud Function
      // → sin cold start). Forzar o doc inexistente → CF, que recalcula con
      // BigQuery y refresca el doc.
      let stats = null;
      if (!forzar) {
        const snapStats = await getDoc(doc(db, 'estadisticas_usuarios_mes', `${email}_${anio}_${mes}`));
        if (snapStats.exists()) {
          const dataStats = snapStats.data();
          stats = { ...dataStats.estadisticas, ultimaActualizacion: dataStats.ultimaActualizacion, fromCache: true };
        }
      }
      if (!stats) {
        stats = await BigQueryService.obtenerEstadisticasPedidosSAC(
          anio,
          mes,
          [email],
          [userData.nombre], // También filtrar por nombre
          forzar
        );
      }

      if (!stats || stats.totalPedidos === 0) {
        setLoading(false);
        return;
      }

      setEstadisticas(stats);
      setFromCache(stats.fromCache || false);

    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
    setLoading(false);
  };

  const formatearFechaHora = (timestamp) => {
    if (!timestamp) return 'No disponible';
    
    try {
      let fecha;
      
      if (timestamp._seconds) {
        fecha = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds) {
        fecha = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        fecha = timestamp.toDate();
      } else {
        fecha = new Date(timestamp);
      }
      
      if (isNaN(fecha.getTime())) {
        return 'No disponible';
      }
      
      const opciones = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Guatemala'
      };
      
      return fecha.toLocaleTimeString('es-GT', opciones);
    } catch (error) {
      return 'No disponible';
    }
  };

  const isMobile = useEsMovil();

  // Números con conteo animado + barras que se llenan al aparecer
  // (misma matemática de "válidos para meta" que se mostraba antes)
  const validosMeta = estadisticas
    ? (estadisticas.estados['POR VERIFICAR']?.cantidad || 0) +
      (estadisticas.estados['POR REVISAR IA']?.cantidad || 0) +
      (estadisticas.estados['CONGELADO']?.cantidad || 0) +
      (estadisticas.estados['POR ENVIAR']?.cantidad || 0) +
      (estadisticas.estados['ENVIADO']?.cantidad || 0) +
      (estadisticas.estados['ENTREGADO']?.cantidad || 0)
    : 0;
  const totalAnimado = useConteoAnimado(estadisticas?.totalPedidos || 0);
  const validosAnimado = useConteoAnimado(validosMeta);
  const tasaAnimada = useConteoAnimado(estadisticas?.mensajes?.conMensaje?.porcentaje || 0);
  // Barras que crecen al aparecer (mismo patrón del faro Estadísticas del Día)
  const [barrasListas, setBarrasListas] = useState(false);
  useEffect(() => {
    if (!estadisticas) return;
    setBarrasListas(false);
    const t = setTimeout(() => setBarrasListas(true), 60);
    return () => clearTimeout(t);
  }, [estadisticas]);

  // Conteos por estado para la dona compartida ({ ESTADO: cantidad })
  const conteosEstados = {};
  Object.entries(estadisticas?.estados || {}).forEach(([e, d]) => {
    conteosEstados[e] = d?.cantidad || 0;
  });

  if (loading) {
    // El skeleton solo aplica a quien va a ver el widget (usuarias de SAC)
    const usuarioActual = usuariosCtx.find(u => u.email === auth.currentUser?.email);
    if (!usuarioActual || usuarioActual.rol !== 'servicio_cliente') return null;
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="estadisticas-pedidos-container" style={{ maxWidth: '1000px', margin: isMobile ? '15px 0' : '20px auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div className="skeleton" style={{ width: '260px', height: '24px', borderRadius: 'var(--radio-sm)' }} />
            <div className="skeleton" style={{ width: '280px', height: '34px', borderRadius: 'var(--radio-sm)' }} />
          </div>
          <div className="pedidos-tiles" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: isMobile ? '8px' : '14px', marginBottom: '14px' }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton" style={{ height: '84px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="pedidos-viz-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? '10px' : '14px' }}>
            {[0, 1].map((i) => (
              <div key={i} className="skeleton" style={{ height: '160px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
        </div>
      </>
    );
  }
  if (!estadisticas || !usuarioData) return null;

  const estadoMasFrecuente = Object.entries(estadisticas.estados)
    .reduce((max, [estado, data]) => {
      return data.cantidad > (max.cantidad || 0) ? 
        { estado, ...data } : max;
    }, {});

  const porcentajeMensajes = estadisticas.mensajes.conMensaje?.porcentaje || 0;
  const cfgPrincipal = estadoMasFrecuente.estado ? estadoConfig(estadoMasFrecuente.estado) : null;
  const colorNotif = porcentajeMensajes >= 80 ? 'var(--color-exito)' :
                     porcentajeMensajes >= 50 ? 'var(--color-advertencia)' : 'var(--color-peligro)';
  const pctValidos = estadisticas.totalPedidos > 0 ? (validosMeta / estadisticas.totalPedidos) * 100 : 0;
  const anulados = estadisticas.estados['ANULADO']?.cantidad || 0;
  const pctAnulados = estadisticas.totalPedidos > 0 ? (anulados / estadisticas.totalPedidos) * 100 : 0;
  const pctPrincipal = estadisticas.totalPedidos > 0 ? ((estadoMasFrecuente.cantidad || 0) / estadisticas.totalPedidos) * 100 : 0;

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="estadisticas-pedidos-container" style={{
        maxWidth: '1000px',
        margin: isMobile ? '15px 0' : '20px auto'
      }}>
        {/* Header estilo dashboard: título a la izquierda, metadata + botón a la
            derecha (en móvil el CSS lo apila y centra) */}
        <div className="estadisticas-pedidos-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: isMobile ? '12px' : '16px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: isMobile ? '18px' : '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><CubeIcon className="w-6 h-6" /></span>
            Mis Estadísticas del Mes
          </h3>
          <div className="estadisticas-pedidos-header-der" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {estadisticas.ultimaActualizacion && (
              <small
                title={fromCache ? 'Datos precalculados (se actualizan solos cada hora)' : 'Recalculado al momento'}
                style={{ color: 'var(--color-texto-secundario)', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
              >
                <span style={{ display: 'inline-flex' }}><ClockIcon className="w-3 h-3" /></span>
                Actualizado {formatearFechaHora(estadisticas.ultimaActualizacion)} · cada hora
              </small>
            )}
            <button
              onClick={() => cargarEstadisticas(true)}
              className="btn-actualizar-pedidos btn-press hover-bright"
              style={{
                height: '38px',
                boxSizing: 'border-box',
                padding: '0 20px',
                backgroundColor: 'var(--color-primario)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radio-sm)',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              title="Forzar recálculo de estadísticas"
            >
              <ArrowPathIcon className="w-4 h-4" /> Actualizar
            </button>
          </div>
        </div>

        {/* Fila de KPI tiles */}
        <div className="pedidos-tiles stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? '8px' : '14px',
          marginBottom: isMobile ? '10px' : '14px'
        }}>
          <div className="card pedidos-tile" style={ESTILO_TILE} title="Todos los pedidos que creaste este mes">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Total pedidos</div>
            <div style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
              {Math.round(totalAnimado)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              en el mes
            </div>
          </div>

          <div className="card pedidos-tile" style={ESTILO_TILE} title="POR VERIFICAR + POR REVISAR IA + CONGELADO + POR ENVIAR + ENVIADO + ENTREGADO">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Válidos para meta</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-exito)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(validosAnimado)}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 'bold', backgroundColor: 'var(--color-exito-fondo)', color: 'var(--color-exito-texto)', padding: '2px 6px', borderRadius: 'var(--radio-sm)', whiteSpace: 'nowrap' }}>
                {pctValidos.toFixed(0)}% del total
              </span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              cuentan para tu bono
            </div>
          </div>

          <div className="card pedidos-tile" style={ESTILO_TILE} title="El estado en el que está la mayoría de tus pedidos del mes">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Estado principal</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: cfgPrincipal ? cfgPrincipal.color : 'var(--color-texto)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {cfgPrincipal && cfgPrincipal.Icon && <cfgPrincipal.Icon className="w-5 h-5" />}
              {cfgPrincipal ? (cfgPrincipal.label || estadoMasFrecuente.estado) : 'N/A'}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px', fontVariantNumeric: 'tabular-nums' }}>
              {estadoMasFrecuente.cantidad || 0} pedidos · {pctPrincipal.toFixed(0)}% del mes
            </div>
          </div>

          <div className="card pedidos-tile" style={ESTILO_TILE} title="Pedidos del mes que terminaron anulados">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Anulados</div>
            <div style={{ fontSize: '26px', fontWeight: 'bold', color: anulados > 0 ? 'var(--color-peligro-texto)' : 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
              {anulados}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px', fontVariantNumeric: 'tabular-nums' }}>
              {pctAnulados.toFixed(1)}% del mes
            </div>
          </div>
        </div>

        {/* Visualizaciones: dona de estados + notificación a clientes */}
        <div className="pedidos-viz-grid stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: isMobile ? '10px' : '14px'
        }}>
          {/* Dona: pedidos por estado (mes) */}
          <div className="card viz-card-pedidos" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex' }}><ChartBarIcon className="w-4 h-4" /></span> Pedidos por estado (mes)
            </div>
            <DonaEstados conteos={conteosEstados} total={estadisticas.totalPedidos} />
          </div>

          {/* Notificación a clientes */}
          <div className="card viz-card-pedidos" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex' }}><BellIcon className="w-4 h-4" /></span> Notificación a clientes
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
              <div style={{
                fontSize: '34px',
                fontWeight: 'bold',
                fontVariantNumeric: 'tabular-nums',
                color: porcentajeMensajes >= 80 ? 'var(--color-exito)' :
                       porcentajeMensajes >= 50 ? 'var(--color-advertencia-texto)' : 'var(--color-peligro-texto)'
              }}>
                {tasaAnimada.toFixed(0)}%
              </div>
              <div style={{ width: '100%', height: '10px', backgroundColor: 'var(--color-borde)', borderRadius: 'var(--radio-sm)', overflow: 'hidden' }}>
                <div style={{
                  width: barrasListas ? `${Math.min(100, porcentajeMensajes)}%` : '0%',
                  height: '100%',
                  backgroundColor: colorNotif,
                  transition: 'width 0.8s ease'
                }} />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', fontVariantNumeric: 'tabular-nums' }}>
                {estadisticas.mensajes.conMensaje?.cantidad || 0} de {estadisticas.totalPedidosMensajes || 0} pedidos notificados
              </div>
            </div>
            <div className="mensaje-motivacional" style={{
              marginTop: '10px',
              padding: isMobile ? '8px' : '10px',
              backgroundColor: porcentajeMensajes >= 80 ? 'var(--color-exito-fondo)' : 'var(--color-advertencia-fondo)',
              borderRadius: 'var(--radio-sm)',
              fontSize: isMobile ? '11px' : '12px',
              textAlign: 'center',
              color: porcentajeMensajes >= 80 ? 'var(--color-exito-texto)' : 'var(--color-advertencia-texto)'
            }}>
              {porcentajeMensajes >= 80 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: 'var(--color-exito-texto)', display: 'inline-flex' }}><StarIcon className="w-4 h-4" /></span>
                  ¡Excelente tasa de notificación! Sigue así.
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: 'var(--color-advertencia-texto)', display: 'inline-flex' }}><ArrowTrendingUpIcon className="w-4 h-4" /></span>
                  Recuerda enviar mensajes de notificación para mejorar la experiencia del cliente.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EstadisticasPedidosWidget;